import { useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

export const UNSAVED_CHANGES_MESSAGE =
  "You have unsaved changes. If you leave now, your changes will be lost.\n\nLeave without saving?";

/**
 * Go to the previous in-app history entry when available; otherwise `fallbackPath`.
 * Uses React Router's history `idx` when present.
 * @param {Function} navigate - react-router navigate
 * @param {string} fallbackPath - used when there is no prior in-app history
 */
export function navigateToPreviousPage(navigate, fallbackPath) {
  const idx = window.history.state?.idx;
  if (typeof idx === "number" && idx > 0) {
    navigate(-1);
    return;
  }
  navigate(fallbackPath);
}

/**
 * Guard in-app navigation and browser leave when a form is dirty.
 * @param {boolean} isDirty
 * @returns {{ tryNavigate: Function, confirmLeave: Function, tryGoBack: Function }}
 */
export function useUnsavedChangesGuard(isDirty) {
  const navigate = useNavigate();
  const isDirtyRef = useRef(!!isDirty);
  isDirtyRef.current = !!isDirty;

  const confirmLeave = useCallback(() => {
    if (!isDirtyRef.current) return true;
    return window.confirm(UNSAVED_CHANGES_MESSAGE);
  }, []);

  const tryNavigate = useCallback(
    (to, options) => {
      if (!confirmLeave()) return false;
      navigate(to, options);
      return true;
    },
    [confirmLeave, navigate]
  );

  const tryGoBack = useCallback(
    (fallbackPath) => {
      if (!confirmLeave()) return false;
      navigateToPreviousPage(navigate, fallbackPath);
      return true;
    },
    [confirmLeave, navigate]
  );

  useEffect(() => {
    if (!isDirty) return undefined;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [isDirty]);

  useEffect(() => {
    if (!isDirty) return undefined;
    const onDocClick = (e) => {
      const a =
        e.target && typeof e.target.closest === "function"
          ? e.target.closest("a[href]")
          : null;
      if (!a) return;
      if (a.target && a.target !== "" && a.target !== "_self") return;
      if (typeof a.hasAttribute === "function" && a.hasAttribute("download")) return;
      if (a.dataset?.skipUnsavedCheck === "1" || a.getAttribute("data-skip-unsaved-check") === "1") {
        return;
      }
      const href = a.getAttribute("href") || "";
      if (!href || href === "#" || href.trim().startsWith("#")) return;
      if (/^javascript:/i.test(href)) return;
      if (/^https?:/i.test(href) || href.startsWith("//")) {
        let url;
        try {
          url = new URL(href, window.location.origin);
        } catch (_err) {
          return;
        }
        if (url.origin !== window.location.origin) {
          e.preventDefault();
          e.stopPropagation();
          if (confirmLeave()) window.location.assign(href);
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        tryNavigate(`${url.pathname}${url.search}${url.hash || ""}`);
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      tryNavigate(href);
    };
    document.addEventListener("click", onDocClick, true);
    return () => document.removeEventListener("click", onDocClick, true);
  }, [isDirty, confirmLeave, tryNavigate]);

  return { tryNavigate, confirmLeave, tryGoBack };
}

/**
 * Compare current values to a baseline snapshot (JSON).
 * Call `resetBaseline()` after load/save so clean state is recorded.
 */
export function useFormDirtyTracker(values) {
  const baselineRef = useRef(null);
  const valuesRef = useRef(values);
  valuesRef.current = values;

  const serialize = (v) => {
    try {
      return JSON.stringify(v);
    } catch (_e) {
      return String(v);
    }
  };

  const current = serialize(values);
  if (baselineRef.current === null) {
    baselineRef.current = current;
  }

  const isDirty = baselineRef.current !== current;

  const resetBaseline = useCallback((nextValues) => {
    baselineRef.current = serialize(
      nextValues !== undefined ? nextValues : valuesRef.current
    );
  }, []);

  return { isDirty, resetBaseline, currentSnapshot: current };
}
